import { useMemo, useState, type FormEvent } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import {
  ArrowLeft,
  Copy,
  Check,
  AlertCircle,
  CheckCircle2,
  Loader2,
  ExternalLink,
  Clock,
  ShieldCheck,
  ShoppingCart,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useCartDetail, useSubmitCartTxid } from '@/hooks/useCart';
import { ApiError } from '@/lib/api-client';
import { formatDate } from '@/lib/utils';

const NETWORK_SHORT_LABELS: Record<string, string> = {
  tron: 'TRC20',
  ethereum: 'ERC20',
  bsc: 'BEP20',
  polygon: 'Polygon',
  arbitrum: 'Arbitrum',
  optimism: 'Optimism',
  base: 'Base',
  avalanche: 'Avalanche',
  solana: 'Solana',
  ton: 'TON',
};

function formatPrice(cents: number | null): string {
  if (cents == null) return '—';
  return `$${(cents / 100).toFixed(2)}`;
}

export function CartCheckoutPage() {
  const { cartId } = useParams({ strict: false }) as { cartId: string };
  const { data, isPending } = useCartDetail(cartId);
  const submit = useSubmitCartTxid(cartId);
  const [txid, setTxid] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // All hooks (including useMemo) must run on every render — bailing
  // out before them changes the hook order and trips React error #310.
  const items = data?.items ?? [];
  const grouped = useMemo(() => {
    const map = new Map<
      string,
      {
        package_id: string;
        package_name: string | null;
        kind: string | null;
        duration_days: number | null;
        unit_cents: number;
        qty: number;
      }
    >();
    for (const it of items) {
      const unit = it.amount_paid ?? 0;
      const k = `${it.package_id}|${unit}`;
      const row = map.get(k) ?? {
        package_id: it.package_id,
        package_name: it.package_name,
        kind: it.kind,
        duration_days: it.duration_days,
        unit_cents: unit,
        qty: 0,
      };
      row.qty++;
      map.set(k, row);
    }
    return Array.from(map.values());
  }, [items]);

  if (isPending || !data) return <div className="text-muted-foreground p-6">Yükleniyor…</div>;

  const cart = data.cart;
  if (!cart.crypto_address) {
    return (
      <div className="p-6 max-w-xl">
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            Bu sepet kripto ödeme değil.{' '}
            <Link to="/packages" className="text-primary hover:underline">
              Paketlere dön
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const network = cart.crypto_network!;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(cart.crypto_address!)}`;

  const onCopy = async () => {
    await navigator.clipboard.writeText(cart.crypto_address!);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await submit.mutateAsync(txid.trim());
      setTxid('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'TXID gönderilemedi');
    }
  };

  const status = cart.status;

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <Button variant="ghost" size="sm" asChild>
          <Link to="/packages">
            <ArrowLeft className="w-4 h-4" /> Paketlere dön
          </Link>
        </Button>
        <h1 className="text-2xl font-semibold mt-2 flex items-center gap-2">
          <ShoppingCart className="w-5 h-5" /> Sepet Ödemesi
        </h1>
        <p className="text-muted-foreground text-sm">
          Sepet <code className="font-mono">{cart.id}</code> · {items.length} kalem
        </p>
      </div>

      {/* Cart summary */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Sepet İçeriği</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-1 text-sm divide-y">
            {grouped.map((row, i) => {
              const isCustomBalance = row.package_id === 'balance-custom';
              const displayName = isCustomBalance
                ? `Bakiye $${(row.unit_cents / 100).toFixed(2)}`
                : row.package_name ?? row.package_id;
              return (
                <li key={i} className="flex items-center justify-between py-2 gap-2">
                  <div className="min-w-0">
                    <div className="font-medium truncate">
                      {displayName}
                      {row.qty > 1 && <span className="text-muted-foreground"> × {row.qty}</span>}
                    </div>
                    {row.duration_days && row.kind !== 'balance' && (
                      <div className="text-xs text-muted-foreground">{row.duration_days} gün</div>
                    )}
                  </div>
                  <div className="font-mono text-sm shrink-0">{formatPrice(row.unit_cents * row.qty)}</div>
                </li>
              );
            })}
          </ul>
          <div className="flex items-center justify-between border-t pt-3 mt-3">
            <span className="text-sm font-medium">Toplam</span>
            <span className="text-xl font-semibold">{formatPrice(cart.total_amount_usd_cents)}</span>
          </div>
        </CardContent>
      </Card>

      {status === 'paid' && (
        <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 p-4 flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-emerald-800 dark:text-emerald-200">Ödeme onaylandı 🎉</p>
            <p className="text-muted-foreground mt-1">
              Seçtiğin ürünler hesabına işlendi. Bakiye yüklemeleri cüzdana eklendi, eklentilerin süreleri başladı.
            </p>
            <Button size="sm" className="mt-3" asChild>
              <Link to="/dashboard">Panele dön</Link>
            </Button>
          </div>
        </div>
      )}

      {status === 'failed' && cart.verification_notes && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-destructive">Ödeme reddedildi</p>
            <p className="text-muted-foreground mt-1">{cart.verification_notes}</p>
          </div>
        </div>
      )}

      {status === 'pending_verification' && (
        <div className="rounded-md border border-primary/30 bg-primary/5 p-4 flex items-start gap-3">
          <Clock className="w-5 h-5 text-primary shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold">TXID alındı — doğrulanıyor</p>
            <p className="text-muted-foreground mt-1">
              Platform yöneticisi transfer'i doğrulayacak. Genellikle 1-24 saat. Onay sonrası tüm kalemler otomatik işlenir.
            </p>
            {cart.crypto_explorer_url && (
              <a
                href={cart.crypto_explorer_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-primary hover:underline mt-2 text-xs"
              >
                Explorer'da gör <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        </div>
      )}

      {status === 'pending' && (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Ödeme Talimatları</CardTitle>
              <CardDescription>
                Aşağıdaki cüzdana <strong>tam olarak {cart.crypto_expected_amount} {cart.crypto_asset}</strong>{' '}
                transfer et. <strong>{NETWORK_SHORT_LABELS[network] ?? network}</strong> ağı kullan — başka ağdan gönderirsen kayıp olur.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-[180px_1fr]">
                <div className="flex justify-center">
                  <img src={qrUrl} alt="QR" className="w-44 h-44 rounded border bg-white p-2" />
                </div>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Adres</Label>
                    <div className="flex gap-2 mt-1">
                      <code className="flex-1 min-w-0 font-mono text-xs bg-muted px-2 py-2 rounded break-all">
                        {cart.crypto_address}
                      </code>
                      <Button size="sm" variant="outline" onClick={onCopy}>
                        {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                      </Button>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Miktar</Label>
                    <div className="text-xl font-semibold font-mono">
                      {cart.crypto_expected_amount} {cart.crypto_asset}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Ağ</Label>
                    <div className="text-sm font-medium">
                      {NETWORK_SHORT_LABELS[network] ?? network}
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-xs space-y-1">
                <p className="font-semibold text-amber-800 dark:text-amber-200 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" /> Dikkat
                </p>
                <ul className="list-disc list-inside space-y-0.5 text-muted-foreground">
                  <li>
                    Tam olarak <strong>{cart.crypto_expected_amount} {cart.crypto_asset}</strong> gönder (eksik/fazla olmasın).
                  </li>
                  <li>Kesinlikle <strong>{NETWORK_SHORT_LABELS[network] ?? network}</strong> ağından gönder.</li>
                  <li>Exchange'den gönderiyorsan ağ seçimini kontrol et.</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <ShieldCheck className="w-4 h-4" /> Ödeme Yaptıktan Sonra
              </CardTitle>
              <CardDescription>
                Transfer tamamlandığında cüzdanın/borsanın verdiği transaction ID'yi (TXID) aşağıya yapıştır.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={onSubmit} className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="txid">Transaction ID (TXID)</Label>
                  <Input
                    id="txid"
                    required
                    value={txid}
                    onChange={(e) => setTxid(e.target.value)}
                    placeholder="örn: 0xabc123… ya da 5VfY…"
                    className="font-mono text-xs"
                  />
                </div>
                {error && (
                  <div className="text-sm text-destructive bg-destructive/10 rounded-md border border-destructive/30 px-3 py-2">
                    {error}
                  </div>
                )}
                <Button type="submit" disabled={submit.isPending || txid.trim().length < 10}>
                  {submit.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  TXID Gönder
                </Button>
              </form>
            </CardContent>
          </Card>
        </>
      )}

      <Card>
        <CardContent className="p-4 text-xs text-muted-foreground flex items-center justify-between">
          <span>Oluşturuldu: {formatDate(cart.created_at)}</span>
          {cart.paid_at && <span>Onaylandı: {formatDate(cart.paid_at)}</span>}
        </CardContent>
      </Card>
    </div>
  );
}
